<template>
  <div 
    id="eodFieldReviewTool" 
    v-if="eodFieldReviewVisible" 
    class="eodFieldReviewTool">
    <div 
      id="eodFieldReviewToolCard" 
      :class="{'card scroll-Container menu-bg-blue-transparent eod-container' :isEodToolOpen}">
      <div class="card-body-padding">
        <!-- Customer Name Label -->
        <div class="eodFieldReviewTitle">
          <strong>EOD Field Review</strong>
          <div class="eodWindowTools">
            <span
              title="Reset"
              id="eodResetButton"
              class="resetButton"
              @click="reset">
              <fa :icon="['fas', 'redo-alt']" size="sm" />
            </span>
            <span
              class="collapseToggle"
              @click="toggleEodTool"
              :title="isEodToolOpen ? 'Collapse' : 'Expand'">
              <fa :icon="['fas', isEodToolOpen ? 'caret-up' : 'caret-down']" size="sm" />
            </span>
          </div>
        </div>
      </div>
      <!-- File Upload Tab -->
      <div 
        class="tab-pane-wrapper" 
        :class="{ 'visible': isEodToolOpen, 'hidden': !isEodToolOpen }">
        <div class="eodFileUploadControl">

          <b-form-group class="form-block">
            <template v-slot:label>
              <strong>Vehicle *</strong>
            </template>
            <b-form-select 
              v-model="selectedVehicle"
              :class="{ 'has-value': selectedVehicle !== null }">
              <option 
                v-for="system in vehicles" 
                :key="system.id" 
                :value="system">
                {{ system.vehicle_name }}
              </option>
              <template #first>
                <b-form-select-option :value="null">
                  Select Vehicle
                </b-form-select-option>
              </template>
            </b-form-select>
          </b-form-group>

          
          <div class="panel" :class="{ 'is-disabled': selectedVehicle === null }">

            <!-- Planned Route -->
            <div>
              <b-form-group v-if="!plannedRouteUploaded" class="form-block">
                <template v-slot:label>
                  <strong>Planned Route File</strong>
                  <img class="icon ml-1 routeFileIcon" src="./../../../assets/routePlanned.svg" />
                </template>

                <b-form-file 
                  ref="refPlannedRouteFile" 
                  id="plannedRouteFile" 
                  accept=".kml,.kmz"
                  placeholder="Select a Planning file..." 
                  drop-placeholder="Drop file here..."
                  @input="handlePlannedFileUpload">
                  <template #button-content>
                    <span>
                      Browse
                      <b-spinner small v-if="loadingPlannedRouteFile" class="mr-2" />
                    </span>
                  </template>
                </b-form-file>
              </b-form-group>
            </div>

            <!-- Completed Route -->
            <div>
              <b-form-group v-if="!completedRouteUploaded" class="form-block">
                <template v-slot:label>
                  <strong>Completed Route File</strong>
                  <img class="icon ml-1 routeFileIcon" src="./../../../assets/routeCompleted.svg" />
                </template>

                <b-form-file 
                  ref="refCompletedRouteFile" 
                  id="completedRouteFile" 
                  accept=".kml,.kmz"
                  placeholder="Select an EOD Trails file..." 
                  drop-placeholder="Drop file here..."
                  @input="handleCompletedFileUpload">
                  <template #button-content>
                    <span>
                      Browse
                      <b-spinner small v-if="loadingCompletedRouteFile" class="mr-2" />
                    </span>
                  </template>
                </b-form-file>
              </b-form-group>
            </div>

            <!-- Remarks -->
            <div>
              <b-form-group class="form-block">
                <template v-slot:label>
                  <strong>Remarks</strong>
                  <fa :icon="['fas', 'clipboard']" class="icon ml-1" />
                </template>
                <b-form-textarea 
                  id="remarks" 
                  @mouseup="enableInput($event)" 
                  @mousedown="enableInput($event)" 
                  rows="6"
                  cols="50" 
                  class="contenteditable-element" 
                  v-model="remarksText" 
                  ref="remarks">
                </b-form-textarea>
              </b-form-group>
            </div>

            <!-- Download Label -->
            <div v-if="uncoveredEodTrialsFileName" class="selectedFileLabel">
              <label class="pt-2">
                Missed segments ready for download
                <!-- <strong class="uncoveredEodFileName">{{uncoveredEodTrialsFileName }}</strong> -->
              </label>
            </div>

            <!-- Download Button -->
            <div>
              <b-form-group class="form-block">
                <div class="modal-button-row">
                  <b-button 
                    class="downloadFiles-btn" 
                    @click="downloadTextFile"
                    :disabled="!isValid || (loadingFile && !plannedRouteFile) ||  (loadingFile && !completedRouteFile)">
                    <b-spinner small v-if="loadingFile" class="mr-2" />
                    Download
                  </b-button>
                </div>
              </b-form-group>
            </div>
          </div>
        </div>
      </div>

      <!-- Spinner Overlay -->
      <transition name="fade">
        <div v-if="loadingFile" class="eodOverlay">
          <div class="eodSpinner-container">
            <div class="spinner"></div>
            <p class="message">{{ loadingMessage }}</p>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<style scoped>
  :root {
  --fs-xxs: 0.65rem;
  --fs-xs: 0.75rem;
  --fs-sm: 0.85rem;
  --fs-md: 1rem;
  --fs-lg: 1.2rem;
  }
  /* Auto-scale everything on smaller screens */
  @media (max-width: 600px) {
  :root {
    --fs-xxs: 0.7rem;
    --fs-xs: 0.8rem;
    --fs-sm: 0.9rem;
    --fs-md: 1rem;
    --fs-lg: 1.1rem;
  }
  }

  ::v-deep(.custom-file) {margin-bottom: 0.4rem;}

/* Match b-form-select with b-form-file appearance */
::v-deep(.custom-select) {
  height: calc(1.5em + 0.75rem + 2px);
  font-size: 0.75rem !important;
  font-family: 'Inter Regular', 'Open Sans', sans-serif;
  padding: 0.375rem 0.75rem;
  color: #495057;
  background-color: #f3f6fa;
  border: 1px solid rgba(6, 60, 109, 0.2);
  border-radius: 8px;
}

/* File input — rounded field with a themed Browse button */
::v-deep(.custom-file-label) {
  border: 1px solid rgba(6, 60, 109, 0.2);
  border-radius: 8px;
  background-color: #f3f6fa;
}
::v-deep(.custom-file-label::after) {
  background: linear-gradient(135deg, #0a5499, #063c6d);
  color: #ffffff;
  border-left: none;
}
::v-deep(.custom-file:hover .custom-file-label) {
  border-color: rgba(10, 84, 153, 0.6);
}
::v-deep(.custom-file-input:focus ~ .custom-file-label) {
  border-color: #0a5499;
  box-shadow: 0 0 0 3px rgba(10, 84, 153, 0.15);
}

/* Section labels (Vehicle *, Planned Route File, Remarks, …) */
::v-deep(.col-form-label),
::v-deep(.form-block label strong) {
  color: #063c6d !important;
  font-weight: 700;
  text-shadow: none !important;
}

/* Focus state */
::v-deep(.custom-select:focus) {
  border-color: #80bdff;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

/* Shared placeholder typography */
::v-deep(.custom-file-label),
::v-deep(.custom-select) {
  font-family: 'Inter Regular', 'Open Sans', sans-serif !important;
  font-size: 0.75rem !important;
  font-weight: 400 !important;
  color: #6c757d !important;
  text-shadow: none !important;
}

::v-deep(.custom-select) {
  margin-bottom: 0.6rem;
}

::v-deep(.custom-select.has-value) {
  color: #063c6d !important;
  font-weight: 600 !important;
}

::v-deep(.custom-select:hover) {
  border-color: #adb5bd;
}

/* FILE placeholder */
::v-deep(.custom-file-label) {
  font-family: 'Inter Regular', 'Open Sans', sans-serif !important;
  font-size: 0.75rem !important;
  font-weight: 400 !important;
  color: #6c757d !important;
}

/* SELECT first option (placeholder) */
::v-deep(.custom-select option[value="null"]) {
  font-family: 'Inter Regular', 'Open Sans', sans-serif !important;
  font-size: 0.75rem !important;
  font-weight: 400 !important;
  color: #6c757d !important;
}

::v-deep(.custom-file-label::after) {
  font-size: 0.75rem !important;
}

::v-deep(.col-form-label){
  padding-bottom: 0rem !important;
}

.tab-pane-wrapper {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 10px 10px;
  min-height: 250px;
  /* transition: all 0.3s ease; */
  overflow-y: auto;
  scrollbar-color: rgb(233, 235, 239, 0.3) rgb(6, 41, 109, 0.3);
  scrollbar-width: thin;
  background-color: #FFF;
}

.is-disabled {
  pointer-events: none;
  opacity: 0.5;
  cursor: not-allowed;
}

.tab-pane-wrapper.visible {
  opacity: 1;
  }
  
  .tab-pane-wrapper.hidden {
  opacity: 0;
  pointer-events: none;
  }
  
  
  .routeFileIcon {
  width: 16px;
  height: 16px;
  }

  .form-group {
  margin-bottom: 0px !important;
  }
  
  .selectedFileLabel {
  text-align: center;
  margin: 0.4rem 0.2rem;
  padding: 6px 8px;
  border-radius: 8px;
  background: rgba(19, 115, 51, 0.1);
  color: #137333;
  font-weight: 600;
  font-size: 0.8rem;
  }
  
  .uncoveredEodFileName {
  color: greenyellow;
  }
  
  .modal-button-row {
  position: static;
  text-align: center;
  width: 100%;
  }
  
  .downloadFiles-btn {
  height: 2.2rem;
  width: 100%;
  margin: 0.6rem 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 0.6rem;
  background: linear-gradient(135deg, #0a5499, #063c6d);
  font-size: 0.95rem !important;
  font-weight: 700;
  letter-spacing: 0.3px;
  color: #ffffff !important;
  box-shadow: 0 2px 8px rgba(6, 60, 109, 0.3);
  transition: filter 0.15s ease, transform 0.12s ease, box-shadow 0.15s ease;
  }
  .downloadFiles-btn:hover:not(:disabled) {
  filter: brightness(1.08);
  transform: translateY(-1px);
  box-shadow: 0 5px 14px rgba(6, 60, 109, 0.38);
  }
  .downloadFiles-btn:active:not(:disabled) {
  transform: translateY(0);
  }
  .downloadFiles-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  box-shadow: none;
  }
  
  .resetButton {
  cursor: pointer;
  }
  
  .card {
  position: relative;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  background: #ffffff;
  border: 1px solid rgba(6, 60, 109, 0.12);
  border-radius: 14px;
  box-shadow: 0 12px 40px rgba(4, 30, 58, 0.28);
  overflow: hidden;
  font-family: 'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  }

  /* Neutralize the inherited text-shadow from .menu-bg-blue-transparent */
  #eodFieldReviewToolCard {
  text-shadow: none !important;
  background-color: transparent;
  border-radius: 0.25rem !important;
  }

  
  .eodFieldReviewTool {
  width: 26%;
  min-width: 360px;
  height: 100%;
  max-width: 480px;
  padding: 0.5rem;
  }
  
  .eodFieldReviewTitle {
  width: 100%;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  font-size: 1.05rem;
  font-weight: 600;
  letter-spacing: 0.2px;
  text-shadow: none;
  }

  .eodWindowTools {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  }

  /* Header control buttons (reset + collapse) */
  .resetButton,
  .collapseToggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.14);
  color: #ffffff;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s ease, transform 0.15s ease;
  }
  .resetButton:hover {
  background: rgba(255, 255, 255, 0.28);
  transform: rotate(-45deg);
  }
  .collapseToggle:hover {
  background: rgba(255, 255, 255, 0.28);
  transform: scale(1.08);
  }

  .card-body-padding {
  padding: 14px 16px;
  background: linear-gradient(135deg, #0a5499, #063c6d);
  border-top-left-radius: 0.25rem;
  border-top-right-radius: 0.25rem;
  }
  
  .contenteditable-element {
  overflow-y: auto;
  font-size: 0.75rem !important;
  color: #063c6d !important;
  width: 100%;
  min-height: 120px;
  resize: vertical;
  padding: 8px 10px;
  background: #f3f6fa;
  border: 1px solid rgba(6, 60, 109, 0.2);
  border-radius: 8px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
  }
  .contenteditable-element:focus {
  background: #ffffff;
  border-color: #0a5499;
  box-shadow: 0 0 0 3px rgba(10, 84, 153, 0.15);
  outline: none;
  }
  
  /* =========================
    SPINNER OVERLAY
   ========================= */
  .eodOverlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  z-index: 10;
  }
  
  .eodSpinner-container {
  text-align: center;
  color: white;
  }
  
  .spinner {
  border: 5px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  width: 45px;
  height: 45px;
  animation: spin 1s linear infinite;
  margin: 0 auto 10px;
  }
  
  @keyframes spin {
  to {
    transform: rotate(360deg);
  }
  }
  
  .message {
  font-size: 1rem;
  color: white;
  margin: 0 auto 10px;
  }
  
  /* 1. Full-width layout on phones */
  @media (max-width: 600px) {
  .eodFieldReviewTool {
    width: 100% !important;
    max-width: 65% !important;
    padding: 0.2rem !important;
  }

  #eodFieldReviewToolCard {
    width: 100% !important;
  }
  }
  
  /* 2. Title row adjustments */
  @media (max-width: 600px) {
  .eodFieldReviewTitle {
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
    font-size: 0.9rem;
  }

  .eodWindowTools {
    gap: 6px;
  }
  }
  
  /* 3. Fix icon sizes */
  @media (max-width: 600px) {
  .routeFileIcon {
    width: 20px !important;
    height: 20px !important;
  }
  }
  
  /* 4. Bootstrap file controls */
  @media (max-width: 600px) {
  ::v-deep(.custom-file-label),
  ::v-deep(.custom-file) {
    font-size: 0.7rem !important;
    padding: 0.3rem 0.5rem !important;
  }
  }
  
  /* 5. Remarks box readable */
  @media (max-width: 600px) {
  .contenteditable-element {
    font-size: 0.8rem !important;
    min-height: 100px;
  }
  }
  
  /* 6. Button alignment */
  @media (max-width: 600px) {
  .modal-button-row {
    position: relative !important;
    width: 100% !important;
    margin-top: 0.5rem;
    padding: 0 0.5rem;
  }

  .submit-btn {
    width: 100% !important;
    max-width: none !important;
  }
  }
  
  /* 7. Scroll container for mobile */
  @media (max-width: 600px) {
  .scroll-Container {
    max-height: calc(100vh - 120px);
    overflow-y: auto;
    padding: 0.5rem;
  }
  }
  
  /* 8. Spinner message readable */
  @media (max-width: 600px) {
  .overlay .message {
    font-size: 0.8rem;
    padding: 0 1rem;
  }
}
</style>

<script>
import axios from 'axios'
import WKT from 'ol/format/WKT'
import Fill from 'ol/style/Fill'
import Polygon from 'ol/geom/Polygon'
import Stroke from 'ol/style/Stroke'
import Style from 'ol/style/Style'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import KML from 'ol/format/KML'
import GeometryCollection from 'ol/geom/GeometryCollection'
import MultiPolygon from 'ol/geom/MultiPolygon'
import Feature from 'ol/Feature'
import { getFormattedDateTime, getKmlFromKmz, removeCustomLayers } from './../../../utils/utils.js'
import toKml from "tokml"
import JSZip from "jszip";
import { saveAs } from "file-saver";

export default {
  name: 'MMP_EODFieldReview',
  components: {
  },
  props: [
    'map',
    'eodFieldReviewVisible',
    'layersListMaxHeight',
    'isAdmin', 
    'user'
  ],
  data() {
    return {
      loadingPlannedRouteFile: false,
      loadingCompletedRouteFile: false,
      loadingFile: false,
      plannedRouteUploaded: false,
      completedRouteUploaded: false,
      plannedPolygonFeature: null,
      completedPolygonFeature: null,
      selectedFile: null,
      plannedRouteFile: null,
      completedRouteFile: null,
      inCompletedPolygonFeature: null,
      polygonInput: null,
      wktObject: null,
      polygonFeature: [],
      isValid: false,
      activeTab: 0,   // 0 = WKT, 1 = File Upload
      allowedFileExtensions: ['kml', 'kmz'],
      allowedKmlGeometries: ['Polygon', 'MultiPolygon', 'LineString', 'MultiLineString'],      
      currentExtent: null,
      polygonVectorLayers: [],
      remarksText: '',
      uncoveredEodTrialsFileName: '',
      loadingMessage:'',
      isEodToolOpen:false,
      vehicles: [],
      selectedVehicle: null,
    }
  },
  methods: {
    getDevices() {
      if (sessionStorage.token) {
        axios
          .get(process.env.API_LOCATION + '/api/dpr/allVehicles', {
            headers: {
              'x-auth-token': sessionStorage.token
            },
            params: {
              isAdmin: this.isAdmin,
              extent: this.user.extent
            }
          })
          .then(response => {
            const content = response.data
            if (content.errors) {
              console.log('Vehicles API Call Error: ' + content.errors)
            } else {
              this.vehicles = content
            }
          })
          .catch(function (error) {
            console.log(error)
          })
      } else {
        console.log('No User Token')
      }
    },
    reset() {
      this.plannedRouteUploaded = false,
      this.completedRouteUploaded = false,
      this.loadingPlannedRouteFile = false,
      this.loadingCompletedRouteFile = false,
      this.plannedPolygonFeature = null,
      this.completedPolygonFeature = null,
      this.loadingFile = false,
      this.selectedFile = null,
      this.polygonInput = null,
      this.polygonFeature = [],
      this.isValid = false,
      this.plannedRouteFile = null,
      this.completedRouteFile = null,
      this.inCompletedPolygonFeature = null,
      this.remarksText = '',
      this.uncoveredEodTrialsFileName = '',
      this.loadingMessage = '',
      this.selectedVehicle = null
      if (this.$refs.refPlannedRouteFile) {
        this.$refs.refPlannedRouteFile.reset()
      }
      if (this.$refs.refCompletedRouteFile) {
        this.$refs.refCompletedRouteFile.reset()
      }
      this.clearEodTrackingLayers()
    },
    toggleEodTool() {
      this.isEodToolOpen = !this.isEodToolOpen
    },
    enableInput(e) {
      e.target.contentEditable = 'true'
      this.$refs.remarks.focus()
    },
    clearEodTrackingLayers() {
     
      if (!this.map) return
     
      removeCustomLayers(this.map, 'eodTracking')

      // hard reset references
      this.polygonVectorLayers.length = 0
      this.polygonVectorLayers = []
      this.plannedRouteFile = null
      this.completedRouteFile = null
      this.plannedPolygonFeature = null
      this.completedPolygonFeature = null
      this.polygonFeature.length = 0
      this.polygonFeature = []

      // Optional but recommended
      this.map.renderSync()
    },
    getExtentRelease(poly) {
      let body = {
        polygon: poly
      }
      if (sessionStorage.token) {
        axios
          .post(process.env.API_LOCATION + '/api/dataDeliveryTool/get_extent', body, {
            headers: {
              'x-auth-token': sessionStorage.token
            }
          })
          .then(response => {
            this.currentExtent = response.data
          })
          .catch(function (error) {
            console.log('MMP_EODFieldReview => getExtentRelease => ', error)
          })
      } else {
        console.log('MMP_EODFieldReview => getExtentRelease => No User Token')
      }
    },
    checkPolygon(fileContents) {
      let checkPolygonFeatures = null
      try {
        if (fileContents) {
          if (Array.isArray(fileContents)) {
            checkPolygonFeatures = fileContents.map(wktText =>
              this.wktObject.readFeature(wktText, {
                dataProjection: 'EPSG:4326',
                featureProjection: 'EPSG:3857'
              })
            )
            this.polygonWkt = fileContents[0]
          } else {
            checkPolygonFeatures = this.wktObject.readFeature(text, {
              dataProjection: 'EPSG:4326',     // Your coords are in lon/lat
              featureProjection: 'EPSG:3857'   // Web Mercator for display
            })
            this.polygonWkt = fileContents
          }
          this.getExtentRelease(this.polygonWkt)
        }
        return checkPolygonFeatures
      } catch (err) {
        console.log("MMP_EODFieldReview => checkPolygon => err => ", err)
        this.loadingFile = false
        this.polygonWkt = null

        if (!this.selectedFile) {
          this.$bvToast.toast('Please Upload a Valid WKT.', {
            autoHideDelay: 7000,
            variant: 'danger',
            solid: true,
            title: 'Invalid WKT',
            toaster: 'b-toaster-top-center'
          })
        } else {
          this.$bvToast.toast('Please Upload a KML/KMZ with Valid WKT.', {
            autoHideDelay: 7000,
            variant: 'danger',
            solid: true,
            title: 'Invalid KML/KMZ',
            toaster: 'b-toaster-top-center'
          })
        }
        return null
      }
    },
    // Convert KML to Geometry text
    async kmlToWKT(kmlText) {
      const kmlFormat = new KML()
      const wktFormat = new WKT()
      const wkts = []

      // Read KML into features Geometry
      const features = kmlFormat.readFeatures(kmlText, {
        dataProjection: 'EPSG:4326',
        featureProjection: 'EPSG:4326'
      })

      features.forEach(f => {
        let geom = f.getGeometry()

        // Handle GeometryCollection / MultiGeometry
        if (geom instanceof GeometryCollection) {
          const polygons = []

          geom.getGeometries().forEach(g => {
            if (g instanceof Polygon) {
              // Force 2D coordinates
              const coords = g.getCoordinates().map(ring =>
                ring.map(c => [c[0], c[1]])
              )
              polygons.push(coords)
            }
          })

          if (polygons.length === 1) {
            geom = new Polygon(polygons[0])
          } else if (polygons.length > 1) {
            geom = new MultiPolygon(polygons)
          }
        } else if (geom instanceof Polygon) {
          // Force 2D coordinates
          const coords = geom.getCoordinates().map(ring =>
            ring.map(c => [c[0], c[1]])
          )
          geom = new Polygon(coords)
        }

        // Write clean WKT
        wkts.push(wktFormat.writeGeometry(geom, { decimals: 6 }))
      })

      return wkts
    },
    async fileUploadHelper(file) {
      this.selectedFile = file
      let resultGeometryFeature = null
      this.isValid = false

      if (this.selectedFile) {
        const ext = this.selectedFile.name.split('.').pop().toLowerCase()
        let kmlText = ''

        if (this.allowedFileExtensions.includes(ext.toLowerCase())) {
          try {
            if (ext.toLowerCase() === 'kmz') {
              kmlText = await getKmlFromKmz(this.selectedFile)
            } else {
              // Directly read KML
              kmlText = await this.selectedFile.text()
            }
            const kmlToWKTText = await this.kmlToWKT(kmlText)
            if (kmlToWKTText) {
              const containsSubstring = kmlToWKTText.some(str =>
                this.allowedKmlGeometries.some(sub =>
                  str.toLowerCase().includes(sub.toLocaleLowerCase())))

              if (containsSubstring) {
                resultGeometryFeature = this.checkPolygon(kmlToWKTText)
                this.isValid = true
              } else {
                this.reset()

                this.$bvToast.toast('The KML/KMZ file does not have Valid Geometries to display. ', {
                  autoHideDelay: 7000,
                  variant: 'danger',
                  solid: true,
                  title: 'Invalid KML/KMZ ',
                  toaster: 'b-toaster-top-center'
                })
              }
            }
            else {
              console.log("MMP_EODFieldReview => fileUploadHelper => Invalid KML/KMZ file")
              throw new Error("MMP_EODFieldReview => fileUploadHelper => No KML found inside KMZ.")
            }

          } catch (err) {
            console.log(" MMP_EODFieldReview => File Upload Error! => ", err)
            this.isValid = false
            this.loadingFile = false
            resultGeometryFeature = null

            this.$bvToast.toast('Please Upload a KML/KMZ with Valid WKT. ' + err, {
              autoHideDelay: 7000,
              variant: 'danger',
              solid: true,
              title: 'Invalid KML/KMZ',
              toaster: 'b-toaster-top-center'
            })

          }
        } else {
          this.isValid = false
          this.loadingFile = false
          this.selectedFile = null
          this.resultGeometryFeature = null

          this.$bvToast.toast('Only valid KML/KMZ files are allowed. ', {
            autoHideDelay: 7000,
            variant: 'danger',
            solid: true,
            title: 'Invalid KML/KMZ',
            toaster: 'b-toaster-top-center'
          })
        }

      } else {
        this.isValid = false
        this.loadingFile = false
        this.selectedFile = null
        resultGeometryFeature = null

        this.$bvToast.toast('Please Upload valid KML/KMZ   file. ', {
          autoHideDelay: 7000,
          variant: 'danger',
          solid: true,
          title: 'Invalid KML/KMZ',
          toaster: 'b-toaster-top-center'
        })
      }
      return resultGeometryFeature
    },
    async handlePlannedFileUpload(file) {
      if (!file) { return }
      this.loadingPlannedRouteFile = true
      this.loadingFile = true
      try {
        this.plannedRouteFile = file
        this.plannedPolygonFeature = await this.fileUploadHelper(file)
        this.polygonFeature.push({ type: 'planned', geometryFeature: this.plannedPolygonFeature })
        this.addPolygonToMap()
        this.loadingFile = false
      } catch (err) {
        console.log("MMP_EODFieldReview => handlePlannedFileUpload error => ", err)
        this.loadingPlannedRouteFile = false
        this.loadingFile = false
      }
    },
    async handleCompletedFileUpload(file) {
      if (!file) { return }
      this.loadingCompletedRouteFile = true
      this.loadingFile = true
      try {
        this.completedRouteFile = file
        this.completedPolygonFeature = await this.fileUploadHelper(file)
        this.polygonFeature.push({ type: 'completed', geometryFeature: this.completedPolygonFeature })
        this.addPolygonToMap()
        let result = await this.calculateGeometryDifference()
      } catch (err) {
        console.log("MMP_EODFieldReview => handleCompletedFileUpload error => ", err)
        this.loadingCompletedRouteFile = false
        this.loadingFile = false
      }
    },
    addPolygonToMap() {
      this.loadingFile = true

      if (!this.polygonFeature) {
        this.loadingFile = false
        return
      }

      try {
        this.polygonFeature.map(geometryFeatureInput => {
          if (geometryFeatureInput) {
            const featureInput = geometryFeatureInput.geometryFeature
            const features = Array.isArray(featureInput)
              ? featureInput
              : [featureInput && featureInput.getType && !featureInput.getId
                ? new Feature({ geometry: featureInput })
                : featureInput]

            const vectorSource = new VectorSource({ features })
            const vectorLayer = new VectorLayer({
              source: vectorSource,
              style: this.styleWKTGeometry(geometryFeatureInput.type),
              zIndex: 1000,
              name: 'eodTracking'
            })

            if (vectorLayer) {
              this.polygonVectorLayers.unshift(vectorLayer)
            }
          }
        })

        this.polygonVectorLayers
          .filter(layer => layer && layer instanceof VectorLayer)
          .forEach(layer => {
            if (!this.map.getLayers().getArray().includes(layer)) {
              this.map.addLayer(layer)
            }
          })

        this.zoomToFitFileUploadPolygons()
      } catch (err) {
        console.log('MMP_EODFieldReview => addPolygonToMap => error => ', err)
      }

    },
    zoomToFitFileUploadPolygons() {
      if (!this.polygonVectorLayers.length) return

      let combinedExtent = null
      const layer = this.polygonVectorLayers[0]
      const source = layer.getSource()
      if (source.getFeatures().length > 0) {
        source.getFeatures().forEach(feature => {
          const featureExtent = feature.getGeometry().getExtent()
          if (!combinedExtent) {
            combinedExtent = [...featureExtent]
          } else {
            combinedExtent = [
              Math.min(combinedExtent[0], featureExtent[0]),
              Math.min(combinedExtent[1], featureExtent[1]),
              Math.max(combinedExtent[2], featureExtent[2]),
              Math.max(combinedExtent[3], featureExtent[3])
            ]
          }
        })
      }

      if (combinedExtent) {
        this.map.getView().fit(combinedExtent, {
          size: this.map.getSize(), // get the actual map size to calculate zoom level for extent bounds
          padding: [100, 100, 100, 100],
          duration: 1000
        })
      }
    },
    styleWKTGeometry(geometryType) {
      let geometryColor = ''

      switch (geometryType) {
        case 'planned': geometryColor = '#B45F06'
          break
        case 'completed': geometryColor = '#007300'
          break
        case 'incomplete': geometryColor = '#990000'
          break
      }
      const style = feature => {
        const geomType = feature.getGeometry().getType()
        switch (geomType) {
          case 'LineString':
          case 'MultiLineString':
            return new Style({
              stroke: new Stroke({
                color: geometryColor,
                width: 3
              })
            })
          case 'Polygon':
          case 'MultiPolygon':
            return new Style({
              fill: new Fill({ color: '#dde32933' }),
              stroke: new Stroke({ color: geometryColor, width: 2 })
            })
          default:
            return new Style({
              stroke: new Stroke({ color: geometryColor, width: 2 })
            })
        }
      }

      return style
    },
    async calculateGeometryDifference() {
      try {
        this.inCompletedPolygonFeature = null
        this.loadingFile = true
        this.loadingMessage = 'Calculating Missed Segments...'
        let result = false

        if (!this.plannedRouteFile || !this.completedRouteFile) {
          this.loadingFile = false
          return
        }

        const plannedFileExt = this.plannedRouteFile ? this.plannedRouteFile.name.split('.').pop().toLowerCase() : ''
        const completedFileExt = this.completedRouteFile ? this.completedRouteFile.name.split('.').pop().toLowerCase() : ''

        const plannedFile = plannedFileExt.toLowerCase() === 'kmz' ? await getKmlFromKmz(this.plannedRouteFile) : await this.plannedRouteFile.text()
        const completedFile = completedFileExt.toLowerCase() === 'kmz' ? await getKmlFromKmz(this.completedRouteFile) : await this.completedRouteFile.text()

        /* Old logic to calculate Geojson Difference in front end without calling backend api */
        //this.inCompletedPolygonFeature = await computeDifferenceFromText(plannedFile, completedFile)

        this.inCompletedPolygonFeature = await this.getKMLDifference(plannedFile, completedFile)

        if (this.inCompletedPolygonFeature && this.inCompletedPolygonFeature.size > 0) {
          this.uncoveredEodTrialsFileName = `EODTrails_UncoveredPath_${getFormattedDateTime()}.kml`
          this.loadingMessage = ''
          result = true
          this.loadingFile = false
          return result
        }
        else {
          this.loadingMessage = ''
          result = true
          this.loadingFile = false
          return result
        }
      } catch (err) {
        console.log('calculateGeometryDifference error => ', err)
        this.loadingFile = false
        return
      }
    },
    async downloadTextFile() {
      if (!this.remarksText.trim() && !this.inCompletedPolygonFeature) {
        return
      }

      const zip = new JSZip()

      const folderName = this.selectedVehicle ? this.selectedVehicle.vehicle_name : 'EOD_Field_Review_Files'; 
      
      const differenceFolder = zip.folder(folderName)

      const downloadFiles = [
        {
          name: `EODTrails_TrackingRemarks_${getFormattedDateTime()}.txt`,
          content: this.remarksText || null,
          type: 'text/plain'
        },
        {
          name: this.uncoveredEodTrialsFileName,
          content: this.inCompletedPolygonFeature || null,
          type: 'application/vnd.google-earth.kml+xml'
        }
      ]

      downloadFiles.forEach(file => {
        if (!file.content) return

        let content = file.content;
        
        if (typeof content === 'object' && !(content instanceof Blob)) {
          content = JSON.stringify(content, null, 2);
         }

        differenceFolder.file(file.name, content)
      })

      // Generate ZIP and download once
      const zipBlob = await zip.generateAsync({ type: 'blob' })

      const zipFileName = `${folderName}_${getFormattedDateTime()}.zip`

      const url = URL.createObjectURL(zipBlob)
      const link = document.createElement('a')

      link.href = url
      link.download = zipFileName
      link.click()

      URL.revokeObjectURL(url) // cleanup
    },
    async getKMLDifference(plannedFile, completedFile) {
      let body = {
        plannedFile: plannedFile,
        completedFile: completedFile
      }
      if (sessionStorage.token) {
        try {
          const kmlResponse = await axios.post(process.env.API_LOCATION + '/api/EODFieldReview/getKMLFileDifference',
            body,
            {
              headers: {
                'x-auth-token': sessionStorage.token
              }
            })

          const kmlBlob = new Blob(
            [kmlResponse.data],
            { type: "application/vnd.google-earth.kml+xml" }
          )

          return kmlBlob
        } catch (error) {
          if (error.response) {
            console.error("Server Error:", error.response.data)
            console.error("Status Code:", error.response.status)
          } else if (error.request) {
            console.error("No Response:", error.request)
          } else {
            console.error("Error Message:", error.message)
          }

          this.$bvToast.toast('There was an error while calculating Difference between planned and completed routes. ', {
            autoHideDelay: 7000,
            variant: 'danger',
            solid: true,
            title: 'Error : Calculate Route Difference',
            toaster: 'b-toaster-top-center'
          })
          return null
        }

      } else {
        console.log('MMP_EODFieldReview => getKMLDifference => No User Token')
      }
    }
  },
  mounted() {
    this.wktObject = new WKT()
    const kmlFormat = new KML()
    this.clearEodTrackingLayers()
    this.reset()
    this.getDevices()
    this.isEodToolOpen = true
  },
  created() {
    this.clearEodTrackingLayers()
    this.reset()
    this.getDevices()
    this.isEodToolOpen = true
  },
  watch: {
    currentExtent: function (newValue) {
      //Clear polygon if 
      if (newValue && newValue.extent_description != this.selectedExtent) {
        this.$emit('clearPolygon')
      }
      this.$emit('datasetSelected', this.currentExtent)
    }
  },
}
</script>
